import { PointSymbol } from "@evergis/sgis/es/symbols/point/Point";
import { StaticImageSymbol } from "@evergis/sgis/es/symbols/point/StaticImageSymbol";
import { SquareSymbol } from "@evergis/sgis/es/symbols/point/Square";
import { MaskedImage } from "@evergis/sgis/es/symbols/point/MaskedImage";
import { Polygon } from "@evergis/sgis/es/features/Polygon";
import { Polyline } from "@evergis/sgis/es/features/Polyline";
import { PolygonSymbol } from "@evergis/sgis/es/symbols/polygon/Simple";
import { BrushFill } from "@evergis/sgis/es/symbols/polygon/BrushFill";
import { ImageFill } from "@evergis/sgis/es/symbols/polygon/ImageFill";
import { plain } from "@evergis/sgis/es/Crs";
import { Poly } from "@evergis/sgis/es/features/Poly";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";

import { SGisPolylineSymbol } from "../SGisPolylineSymbol";
import { SGisMiterSymbol } from "../SGisMiterSymbol";

import {
  heartbeatLineFeature,
  directLineFeature,
  pointFeature,
  hexagonPolygonFeature,
  squarePolygonFeature,
  viewBoxSize,
} from "./previewFeatures";

export type PointSymbolType = PointSymbol | StaticImageSymbol | SquareSymbol | MaskedImage;
export type ImageSymbolType = StaticImageSymbol | MaskedImage;
export type PolygonSymbolType = PolygonSymbol | BrushFill | ImageFill;
export type PolylineSymbolType = SGisPolylineSymbol | SGisMiterSymbol;
export type SymbolType = PointSymbolType | PolygonSymbolType | PolylineSymbolType;
export type FeatureToRender = Poly | PointFeature;

export function isPointSymbol(symbol: SymbolType): symbol is PointSymbolType {
  return (
    symbol instanceof PointSymbol ||
    symbol instanceof StaticImageSymbol ||
    symbol instanceof SquareSymbol ||
    symbol instanceof MaskedImage
  );
}

export function isImageSymbol(symbol: SymbolType): symbol is ImageSymbolType {
  return symbol instanceof StaticImageSymbol || symbol instanceof MaskedImage;
}

export function isPolygonSymbol(symbol: SymbolType): symbol is PolygonSymbolType {
  return symbol instanceof PolygonSymbol || symbol instanceof BrushFill || symbol instanceof ImageFill;
}

export function isPolylineSymbol(symbol: SymbolType): symbol is PolylineSymbolType {
  return symbol instanceof SGisPolylineSymbol;
}

export function getFeatureToRender(symbol: SymbolType, isLegend: boolean): FeatureToRender {
  if (isPointSymbol(symbol)) {
    return pointFeature;
  }

  if (isPolygonSymbol(symbol)) {
    return isLegend ? squarePolygonFeature : hexagonPolygonFeature;
  }

  if (isPolylineSymbol(symbol)) {
    return isLegend ? directLineFeature : heartbeatLineFeature;
  }
}

export function getSymbolRenderers(symbol: SymbolType, size: number = viewBoxSize, feature?: FeatureToRender) {
  const resolution = viewBoxSize / size;

  if (feature) {
    return symbol.renderFunction(feature as PointFeature & Polygon & Polyline, resolution, plain);
  }
}
